import { Modal, message, Select } from 'antd';
import {
  ChevronDown,
  ChevronUp,
  Copy,
  KeyRound,
  Plus,
  RefreshCcw,
  ShieldOff,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { confirm } from '@/components/common/ConfirmDialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { adminTokenApi } from '@/services/adminTokenApi';
import { AdminTokenModeMismatchError } from '@/services/adminTokenContract';
import type {
  AdminToken,
  AdminTokenAccessMode,
  AdminTokenModule,
} from '@/types';

const ACCESS_OPTIONS = [
  { label: '无权限', value: 'none' },
  { label: '只读', value: 'read' },
  { label: '可写', value: 'write' },
];
const GLOBAL_ACCESS_OPTIONS = ACCESS_OPTIONS.slice(1);
const READ_ACCESS_OPTIONS = ACCESS_OPTIONS.slice(0, 2);

const MODULE_LABELS: Record<AdminTokenModule, string> = {
  basic: '管理基础',
  access: '用户准入与配置',
  commerce: '商业化',
  app: '应用与市场',
  llm: 'LLM 配置',
  release: '发布管理',
  computer: 'Computer 与任务',
  diagnostics: '诊断',
};
const MODULES = (Object.keys(MODULE_LABELS) as AdminTokenModule[]).filter(
  (module) => module !== 'commerce',
);
const moduleModesFor = (mode: AdminTokenAccessMode) =>
  Object.fromEntries(MODULES.map((module) => [module, mode])) as Record<
    AdminTokenModule,
    'none' | AdminTokenAccessMode
  >;

const TTL_OPTIONS = [
  { label: '7 天', value: 7 },
  { label: '30 天', value: 30 },
  { label: '90 天', value: 90 },
  { label: '180 天', value: 180 },
];

export function AdminTokensPage() {
  const [tokens, setTokens] = useState<AdminToken[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [note, setNote] = useState('');
  const [accessMode, setAccessMode] = useState<AdminTokenAccessMode>('read');
  const [detailedControl, setDetailedControl] = useState(false);
  const [moduleModes, setModuleModes] = useState(() => moduleModesFor('read'));
  const [supportsScopedTokens, setSupportsScopedTokens] = useState(false);
  const [expiresInDays, setExpiresInDays] = useState(90);
  const [createdTokenValue, setCreatedTokenValue] = useState('');

  const activeTokens = useMemo(
    () => tokens.filter((token) => tokenStatus(token) === 'active').length,
    [tokens],
  );
  const moduleGrants = MODULES.filter(
    (module) => !detailedControl || moduleModes[module] !== 'none',
  ).map((module) => ({
    module,
    accessMode: detailedControl
      ? (moduleModes[module] as AdminTokenAccessMode)
      : accessMode,
  }));

  const handleAccessModeChange = (mode: AdminTokenAccessMode) => {
    setAccessMode(mode);
    setModuleModes((current) => {
      if (!detailedControl) return moduleModesFor(mode);
      return Object.fromEntries(
        MODULES.map((module) => [
          module,
          current[module] === accessMode ? mode : current[module],
        ]),
      ) as typeof current;
    });
  };

  const loadTokens = useCallback(async () => {
    setLoading(true);
    setLoadFailed(false);
    setSupportsScopedTokens(false);
    try {
      const response = await adminTokenApi.listTokens();
      setTokens(response.tokens);
      setSupportsScopedTokens(
        response.supportedAccessModes.includes('read') &&
          response.supportedAccessModes.includes('write') &&
          MODULES.every((module) => response.supportedModules.includes(module)),
      );
    } catch (error) {
      setLoadFailed(true);
      console.error('加载管理密钥失败:', error);
      message.error('加载管理密钥失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadTokens();
  }, [loadTokens]);

  const handleCreate = async () => {
    if (
      creating ||
      loading ||
      !supportsScopedTokens ||
      moduleGrants.length === 0
    )
      return;
    setCreating(true);
    try {
      const response = await adminTokenApi.createToken({
        note,
        expiresInDays,
        accessMode,
        moduleGrants,
      });
      setCreateOpen(false);
      setNote('');
      setAccessMode('read');
      setDetailedControl(false);
      setModuleModes(moduleModesFor('read'));
      setExpiresInDays(90);
      setCreatedTokenValue(response.tokenValue);
      await loadTokens();
      message.success('管理密钥已创建');
    } catch (error) {
      console.error('创建管理密钥失败:', error);
      if (error instanceof AdminTokenModeMismatchError) {
        setCreateOpen(false);
        await loadTokens();
        message.error(error.message);
      } else {
        message.error('创建管理密钥失败');
      }
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = (token: AdminToken) => {
    confirm({
      title: '确认撤销管理密钥',
      content: (
        <div className="space-y-2 text-sm">
          <p>
            密钥 <span className="font-mono">{token.tokenId}</span> 撤销后，
            使用它的 CLI 会立即失效。
          </p>
          <p className="text-muted-foreground">
            此操作不会影响已发布的应用版本。
          </p>
        </div>
      ),
      okText: '确认撤销',
      cancelText: '取消',
      type: 'error',
      onOk: async () => {
        try {
          await adminTokenApi.revokeToken(token.tokenId);
          await loadTokens();
          message.success('管理密钥已撤销');
        } catch (error) {
          console.error('撤销管理密钥失败:', error);
          message.error('撤销管理密钥失败');
        }
      },
    });
  };

  const copyTokenValue = async () => {
    try {
      await navigator.clipboard.writeText(createdTokenValue);
      message.success('密钥已复制');
    } catch {
      message.error('复制失败，请手动复制');
    }
  };

  return (
    <>
      <div className="rounded-lg border border-border bg-white shadow-sm">
        <div className="space-y-6 p-6">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div className="space-y-2">
              <div className="flex items-center gap-3">
                <h1 className="text-lg font-semibold text-foreground">
                  管理密钥
                </h1>
                <Badge variant="info">CLI</Badge>
              </div>
              <p className="max-w-3xl text-sm text-muted-foreground">
                用于 `tutti-ops` CLI 调用管理后台接口。密钥只在创建时展示一次，
                可按模块设置只读或可写权限，后续只能撤销后重新创建。
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={() => void loadTokens()}
                loading={loading}
              >
                <RefreshCcw className="mr-2 h-4 w-4" />
                刷新
              </Button>
              <Button
                disabled={loading || !supportsScopedTokens}
                onClick={() => {
                  setAccessMode('read');
                  setDetailedControl(false);
                  setModuleModes(moduleModesFor('read'));
                  setCreateOpen(true);
                }}
              >
                <Plus className="mr-2 h-4 w-4" />
                新增密钥
              </Button>
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-3">
            <div className="rounded-xl border border-border bg-slate-50 p-4">
              <p className="text-xs font-medium uppercase tracking-[0.12em] text-muted-foreground">
                总数
              </p>
              <p className="mt-2 text-2xl font-semibold text-foreground">
                {tokens.length}
              </p>
            </div>
            <div className="rounded-xl border border-border bg-slate-50 p-4">
              <p className="text-xs font-medium uppercase tracking-[0.12em] text-muted-foreground">
                可用
              </p>
              <p className="mt-2 text-2xl font-semibold text-foreground">
                {activeTokens}
              </p>
            </div>
            <div className="rounded-xl border border-border bg-slate-50 p-4">
              <p className="text-xs font-medium uppercase tracking-[0.12em] text-muted-foreground">
                默认有效期
              </p>
              <p className="mt-2 text-2xl font-semibold text-foreground">
                90 天
              </p>
            </div>
          </div>

          {!loading && !supportsScopedTokens && (
            <output className="text-sm text-muted-foreground">
              {loadFailed
                ? '加载密钥及模块权限失败，请刷新后重试。'
                : '服务端尚未支持密钥模块权限，请升级服务端后创建密钥。'}
            </output>
          )}
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>密钥</TableHead>
                <TableHead>备注</TableHead>
                <TableHead>权限</TableHead>
                <TableHead>创建人</TableHead>
                <TableHead>过期时间</TableHead>
                <TableHead>最近使用</TableHead>
                <TableHead>状态</TableHead>
                <TableHead>操作</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {tokens.map((token) => {
                const status = tokenStatus(token);
                return (
                  <TableRow key={token.tokenId}>
                    <TableCell className="font-mono">{token.tokenId}</TableCell>
                    <TableCell>{token.note || '-'}</TableCell>
                    <TableCell>
                      <Badge
                        variant={
                          token.accessMode === 'read' ? 'info' : 'warning'
                        }
                      >
                        {token.accessMode === 'read' ? '只读' : '可写'}
                      </Badge>
                      <div className="mt-1 text-xs text-muted-foreground">
                        {token.moduleGrants.length === 0 ||
                        (token.moduleGrants.length ===
                          Object.keys(MODULE_LABELS).length &&
                          token.moduleGrants.every(
                            (grant) => grant.accessMode === token.accessMode,
                          ))
                          ? '全部模块'
                          : token.moduleGrants.length === MODULES.length &&
                              token.moduleGrants.every(
                                (grant) =>
                                  grant.module !== 'commerce' &&
                                  grant.accessMode === token.accessMode,
                              )
                            ? '全部非商业化模块'
                            : token.moduleGrants
                                .map(
                                  (grant) =>
                                    `${MODULE_LABELS[grant.module] ?? grant.module}：${grant.accessMode === 'write' ? '可写' : '只读'}`,
                                )
                                .join('、')}
                      </div>
                    </TableCell>
                    <TableCell className="font-mono">
                      {token.createdBy || '-'}
                    </TableCell>
                    <TableCell>{formatTime(token.expiresAt)}</TableCell>
                    <TableCell>{formatTime(token.lastUsedAt)}</TableCell>
                    <TableCell>
                      {status === 'active' && (
                        <Badge variant="success">可用</Badge>
                      )}
                      {status === 'expired' && (
                        <Badge variant="warning">已过期</Badge>
                      )}
                      {status === 'revoked' && (
                        <Badge variant="destructive">已撤销</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="icon"
                        disabled={status !== 'active'}
                        title="撤销"
                        onClick={() => handleRevoke(token)}
                      >
                        <ShieldOff className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
              {tokens.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={8}
                    className="py-10 text-center text-muted-foreground"
                  >
                    暂无管理密钥
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      <Dialog
        open={createOpen}
        onOpenChange={(open) => {
          if (!creating) setCreateOpen(open);
        }}
      >
        <DialogContent title={null}>
          <DialogHeader>
            <DialogTitle>新增管理密钥</DialogTitle>
            <DialogDescription>
              创建后只展示一次完整 token，请及时复制到 CLI 登录流程。
            </DialogDescription>
          </DialogHeader>
          <div className="mt-6 max-h-[60vh] space-y-4 overflow-y-auto pr-1">
            <div className="space-y-2">
              <Label htmlFor="token-access-mode">整体权限</Label>
              <p className="text-sm text-muted-foreground">
                默认允许非商业化模块只读；此页面创建的密钥不包含商业化权限。整体权限决定最高权限，需要限制具体模块时可展开详细控制。
              </p>
              <Select<AdminTokenAccessMode>
                id="token-access-mode"
                aria-label="整体权限"
                className="w-full"
                disabled={creating}
                value={accessMode}
                options={GLOBAL_ACCESS_OPTIONS}
                onChange={handleAccessModeChange}
              />
              <Button
                variant="outline"
                disabled={creating}
                onClick={() => {
                  setDetailedControl(!detailedControl);
                  setModuleModes(moduleModesFor(accessMode));
                }}
              >
                {detailedControl ? (
                  <ChevronUp className="mr-2 h-4 w-4" />
                ) : (
                  <ChevronDown className="mr-2 h-4 w-4" />
                )}
                {detailedControl ? '使用整体权限' : '详细控制模块权限'}
              </Button>
              {detailedControl && (
                <div className="space-y-3 rounded-md border border-border p-3">
                  <p className="text-sm text-muted-foreground">
                    可单独关闭模块或收窄权限；密钥仍受创建者的管理员权限限制，也不能签发新密钥。
                  </p>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    {MODULES.map((module) => (
                      <div key={module} className="space-y-1">
                        <Label htmlFor={`token-module-${module}`}>
                          {MODULE_LABELS[module]}
                        </Label>
                        <Select<'none' | AdminTokenAccessMode>
                          id={`token-module-${module}`}
                          aria-label={`${MODULE_LABELS[module]}权限`}
                          className="w-full"
                          disabled={creating}
                          value={moduleModes[module]}
                          options={
                            accessMode === 'read'
                              ? READ_ACCESS_OPTIONS
                              : ACCESS_OPTIONS
                          }
                          onChange={(value) =>
                            setModuleModes((current) => ({
                              ...current,
                              [module]: value,
                            }))
                          }
                        />
                      </div>
                    ))}
                  </div>
                  {moduleGrants.length === 0 && (
                    <p className="text-sm text-destructive">
                      请至少授权一个模块。
                    </p>
                  )}
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="token-note">备注</Label>
              <Input
                id="token-note"
                disabled={creating}
                value={note}
                onChange={(event) => setNote(event.target.value)}
                placeholder="例如：vibe-design 发布 CLI"
              />
            </div>
            <div className="space-y-2">
              <Label>有效期</Label>
              <Select
                className="w-full"
                disabled={creating}
                value={expiresInDays}
                options={TTL_OPTIONS}
                onChange={setExpiresInDays}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              disabled={creating}
              onClick={() => setCreateOpen(false)}
            >
              取消
            </Button>
            <Button
              loading={creating}
              disabled={
                loading || !supportsScopedTokens || moduleGrants.length === 0
              }
              onClick={() => void handleCreate()}
            >
              <KeyRound className="mr-2 h-4 w-4" />
              创建
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Modal
        open={Boolean(createdTokenValue)}
        title="复制管理密钥"
        onCancel={() => setCreatedTokenValue('')}
        footer={[
          <Button key="copy" onClick={() => void copyTokenValue()}>
            <Copy className="mr-2 h-4 w-4" />
            复制
          </Button>,
          <Button
            key="close"
            variant="outline"
            onClick={() => setCreatedTokenValue('')}
          >
            已保存
          </Button>,
        ]}
      >
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">
            关闭后无法再次查看完整 token。
          </p>
          <pre className="max-h-40 overflow-auto rounded-md border border-border bg-slate-50 p-3 font-mono text-xs text-foreground">
            {createdTokenValue}
          </pre>
        </div>
      </Modal>
    </>
  );
}

function tokenStatus(token: AdminToken): 'active' | 'expired' | 'revoked' {
  if (token.revokedAt) {
    return 'revoked';
  }
  if (token.expiresAt > 0 && token.expiresAt <= Math.floor(Date.now() / 1000)) {
    return 'expired';
  }
  return 'active';
}

function formatTime(value: number | undefined) {
  if (!value) {
    return '-';
  }
  return new Intl.DateTimeFormat('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(value * 1000);
}
